import { ExternalLinkIcon } from '@chakra-ui/icons'
import {
  Badge,
  Box,
  Flex,
  Heading,
  Link,
  Table,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react'
import { Ellipsis } from 'BrokerTsLib/src/components'
import type { AppInfoMessage, AppInfoMessagePagedQuery } from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import { useGetAllAppInfoMessages } from 'BrokerTsLib/src/hooks/useAppInfoMessage'
import { createContext, useContext, useEffect, useState } from 'react'
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom'

import type { IPaginationContext } from 'BrokerTsLib/src/interfaces/Pagination/Pagination'
import { Loading } from '../../components/Loading'
import { Pagination } from '../../components/Pagination'
import { TableWrapper } from '../../components/TableWrapper'
import type { IAvailableAppsProps } from '../../utils/availableApps'
import { useGetAvailableApps } from '../../utils/availableApps'
import { formatDate } from '../../utils/formatDate'
import { NewsItemTypeBadge } from './NewsItem'

type NewsAdminTableProps = {
  news: Array<AppInfoMessage>,
  context: React.Context<IPaginationContext<AppInfoMessagePagedQuery>>
  app: IAvailableAppsProps
}

const NewsAdminTable = ({
  news,
  context,
  app
}: NewsAdminTableProps) => {
  const navigate = useNavigate()
  const { currentPage, pageSize, totalCount, setCurrentPage, reload, loading, ...rest } = useContext(context)

  return (
    <TableWrapper>
      <Table>
        <Thead>
          <Tr>
            <Th>
              Datum
              <br />
              Nadpis
            </Th>
            <Th></Th>
            <Th>Typ</Th>
          </Tr>
        </Thead>
        <Tbody>
          {!loading && ((news?.length ?? 0) > 0) &&
            news.map(({ messageImportance: importance, messageDate: date, messageShow: show, messageHeader: header, type/*, company */, ...rest }) => {
              return (
                <Tr
                  key={rest.id}
                  onClick={() => navigate(`/${app.id}/news/${rest.id}`)}
                  cursor='pointer'
                >
                  {/* <Td>{importance && importance == AppInfoMessageImportanceTypes.High &&
                    <Alert color="red.500" />}</Td> */}
                  <Td>
                    <Flex sx={{ gap: 1 }}>
                      {date && (
                        <Text sx={{ color: 'blue.600', fontSize: 'sm', fontWeight: 'medium' }}>
                          {formatDate({ date: date })}
                        </Text>
                      )}
                      {show && <Badge colorScheme="blue">Zobrazená</Badge>}
                      {!show && <Badge colorScheme="gray">Skrytá</Badge>}
                    </Flex>

                    <Ellipsis
                      sx={{
                        maxW: '320px',
                        fontSize: 'sm',
                        fontWeight: 'semibold'
                      }}
                      noOfLines={{ base: 2, md: 1 }}
                    >
                      {header}
                    </Ellipsis>
                  </Td>
                  <Td>
                    <Flex sx={{ gap: 1 }}>
                      {importance && <NewsItemTypeBadge priority={importance} />}
                    </Flex>
                  </Td>
                  <Td>
                    {type && <Badge colorScheme='blue'>{type}</Badge>}
                  </Td>
                </Tr>
              )
            })}

          {!loading && ((news?.length ?? 0) == 0) && (
            <Tr>
              <Td colSpan={3} py={8} textAlign={'center'}>
                <Text>Žádné aktuality k zobrazení</Text>
              </Td>
            </Tr>
          )}

          {loading && (
            <Tr>
              <Td colSpan={3} py={8}>
                <Loading large={true} />
              </Td>
            </Tr>
          )}
        </Tbody>
        <Tfoot>
          <Tr>
            <Th colSpan={3}>
              <Pagination totalCount={totalCount} currentPage={currentPage} setCurrentPage={setCurrentPage} maxButtonCount={5} pageSize={pageSize} />
            </Th>
          </Tr>
        </Tfoot>
      </Table>
    </TableWrapper >
  )
}

const NewsAdminByApp = ({ app }: { app: IAvailableAppsProps }) => {


  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(5)
  const [deletedCounter, setDeletedCounter] = useState(0)
  const [filter, setFilter] = useState<AppInfoMessagePagedQuery>({
    product: app.product!,
    pageIndex: 1,
  })

  const { mutateAsync: getMessages, data: dataMessages, isLoading: isLoading } = useGetAllAppInfoMessages()
  const items = dataMessages?.data ?? {} as any

  const reloadData = () => setDeletedCounter(deletedCounter + 1)

  useEffect(() => {
    setFilter({ ...filter, pageSize: pageSize, pageIndex: currentPage })
  }, [pageSize, currentPage])

  useEffect(() => {
    if (filter.pageSize)
      getMessages(filter)
  }, [filter, deletedCounter])

  let context
  switch (app.product) {
    case Product.VehicleInsurance:
      context = appInfoMessageCarContext
      break
    case Product.TravelInsurance:
      context = appInfoMessageTravelContext
      break
    case Product.PropertyInsurance:
    case Product.LiabilityInsurance:
      context = appInfoMessagePropertyContext
      break
    default:
      context = null
      break
  }
  if (context == null)
    return null
  return (
    <context.Provider value={
      {
        loading: isLoading,
        totalCount: items?.totalCount ?? 0,
        currentPage: currentPage,
        pageSize: pageSize,
        filter: filter,
        setCurrentPage: setCurrentPage,
        setPageSize: setPageSize,
        updateFilter: f => setFilter(f),
        reload: reloadData
      }
    }>
      <NewsAdminTable news={items?.items ?? []} context={context} app={app} />
    </context.Provider>)
}

export const appInfoMessageCarContext = createContext<IPaginationContext<AppInfoMessagePagedQuery>>({} as IPaginationContext<AppInfoMessagePagedQuery>)
export const appInfoMessagePropertyContext = createContext<IPaginationContext<AppInfoMessagePagedQuery>>({} as IPaginationContext<AppInfoMessagePagedQuery>)
export const appInfoMessageTravelContext = createContext<IPaginationContext<AppInfoMessagePagedQuery>>({} as IPaginationContext<AppInfoMessagePagedQuery>)

export const NewsAdminList = () => {
  const availableApps = useGetAvailableApps()

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 8,
        gridTemplateColumns: { base: '1fr', xl: 'repeat(2, 1fr)' },
        placeItems: 'stretch',
      }}
    >
      {availableApps
        .filter((app) => ['carkulka', 'chaloupka', 'cestovko'].includes(app.id))
        .map((app) => {
          return (
            <Box key={app.id} sx={{ display: 'grid', gap: 4 }}>
              <Link as={ReactRouterLink} to={`/${app.id}/news`}>
                <Flex alignItems='center'>
                  <Heading sx={{ fontSize: 'xl', fontWeight: 'normal' }}>
                    {app.name}
                  </Heading>
                  <ExternalLinkIcon mx={2} />
                </Flex>
              </Link>
              <NewsAdminByApp app={app} />
            </Box>
          )
        })}
    </Box >
  )
}
